import { getNextSplitPaymentToken } from '@/lib/checkout/payments/adyen';
import { clearSplitPaymentInFlight } from '@/lib/checkout/payments/splitPaymentSession';
import { checkoutPaths } from '@/lib/paths';
import { captureLastCompletedOrder } from '@/lib/gtm/purchaseTracking.client';
import { devError, devLog } from '@/lib/utils/devLog';
import { hasSplitPaymentInFlight } from '@/states/paymentGatewaysState';

import type { SplitPaymentCheckResponse } from '@/app/api/checkout/split-payment-check/route';

export function gatewayConfigsHaveRemainingBalance(
  gatewayConfigs: Array<{ data?: unknown } | null | undefined> | null | undefined
): boolean {
  return (
    gatewayConfigs?.some((config) => {
      const data = config?.data as Record<string, unknown> | null;
      return Boolean(data?.nextPaymentMinAmount && Number(data.nextPaymentMinAmount) > 0);
    }) ?? false
  );
}

export function resolveSplitPaymentRedirectUrl(params: {
  orderId: string;
  amountRemainingToBePaid: boolean;
  payPerLinkToken: string | null;
}): string {
  const { orderId, amountRemainingToBePaid, payPerLinkToken } = params;

  if (!amountRemainingToBePaid) {
    return checkoutPaths.confirmation(orderId);
  }

  if (payPerLinkToken) {
    return `/store/order/payment?token=${payPerLinkToken}&order-id=${orderId}`;
  }

  return checkoutPaths.confirmation(orderId);
}

/**
 * Fetch split payment status from the BFF endpoint.
 *
 * All Saleor calls (paymentGatewayInitialize, PayPerLinkOrderData) happen
 * server-side in the BFF route, which has access to SALEOR_APP_TOKEN.
 */
async function fetchSplitPaymentCheck(params: {
  orderId: string;
  checkoutId?: string;
  totalAmount?: number;
  countryCode: string;
  locale: string;
  payPerLinkToken?: string;
}): Promise<SplitPaymentCheckResponse> {
  const res = await fetch('/api/checkout/split-payment-check', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });

  if (!res.ok) {
    throw new Error(`Split payment check failed: ${res.status}`);
  }

  const json = await res.json();
  if (!json.success) {
    throw new Error(json.errors?.[0]?.message ?? 'Split payment check failed');
  }

  return json.data as SplitPaymentCheckResponse;
}

interface AuthorisedPaymentRedirectParams {
  orderId: string;
  checkoutId?: string;
  /** Omit when unknown (redirect return) — the BFF then reads the order total. */
  totalAmount?: number;
  countryCode: string;
  locale: string;
  /** Proves order ownership when the payer has no checkout cookie (pay-per-link). */
  payPerLinkToken?: string;
  transactionData: Record<string, unknown> | null;
  logPrefix?: string;
}

async function resolveRemainingBalance(
  params: AuthorisedPaymentRedirectParams
): Promise<SplitPaymentCheckResponse> {
  const { transactionData, logPrefix = '[Payment]', ...checkParams } = params;
  const tokenFromTransaction = getNextSplitPaymentToken(transactionData) ?? null;

  let check: SplitPaymentCheckResponse;
  try {
    check = await fetchSplitPaymentCheck(checkParams);
  } catch (err) {
    // Adyen only returns an instalment token while a balance is due, so trust it
    // rather than sending a partially paid order to the confirmation page.
    if (!tokenFromTransaction) {
      throw err;
    }
    devError(`${logPrefix} Balance check unavailable, trusting Adyen instalment token:`, err);
    check = { amountRemainingToBePaid: true, payPerLinkToken: tokenFromTransaction };
  }

  const payPerLinkToken = tokenFromTransaction ?? check.payPerLinkToken ?? null;

  devLog(`${logPrefix} After split payment check:`, {
    amountRemainingToBePaid: check.amountRemainingToBePaid,
    tokenFromTransaction,
    tokenFromServer: check.payPerLinkToken,
    payPerLinkToken,
  });

  return { amountRemainingToBePaid: check.amountRemainingToBePaid, payPerLinkToken };
}

export async function fetchAuthorisedPaymentRedirectUrl(
  params: AuthorisedPaymentRedirectParams
): Promise<string> {
  const { amountRemainingToBePaid, payPerLinkToken } = await resolveRemainingBalance(params);

  return resolveSplitPaymentRedirectUrl({
    orderId: params.orderId,
    amountRemainingToBePaid,
    payPerLinkToken,
  });
}

export function isOrderConfirmationRedirect(orderId: string, redirectUrl: string): boolean {
  return redirectUrl.startsWith(checkoutPaths.confirmation(orderId));
}

export type AuthorisedPaymentRedirectOutcome =
  | { kind: 'pay-per-link'; url: string }
  | { kind: 'confirmation' }
  | { kind: 'remaining-balance-missing-token' };

export async function resolveAuthorisedPaymentRedirectOutcome(
  params: AuthorisedPaymentRedirectParams
): Promise<AuthorisedPaymentRedirectOutcome> {
  const { orderId } = params;
  const { amountRemainingToBePaid, payPerLinkToken } = await resolveRemainingBalance(params);

  if (amountRemainingToBePaid && !payPerLinkToken) {
    return { kind: 'remaining-balance-missing-token' };
  }

  if (amountRemainingToBePaid && payPerLinkToken) {
    return {
      kind: 'pay-per-link',
      url: resolveSplitPaymentRedirectUrl({
        orderId,
        amountRemainingToBePaid: true,
        payPerLinkToken,
      }),
    };
  }

  return { kind: 'confirmation' };
}

export async function executeAuthorisedPaymentRedirect(params: {
  orderId: string;
  checkoutId?: string;
  totalAmount?: number;
  countryCode: string;
  locale: string;
  /** Proves order ownership when the payer has no checkout cookie (pay-per-link). */
  payPerLinkToken?: string;
  transactionData?: Record<string, unknown>;
  logPrefix?: string;
  router: { replace: (url: string) => void };
  onRemainingBalanceMissingToken?: () => void;
}): Promise<void> {
  const {
    orderId,
    checkoutId,
    totalAmount,
    countryCode,
    locale,
    payPerLinkToken,
    transactionData = {},
    logPrefix = '[Payment]',
    router,
    onRemainingBalanceMissingToken,
  } = params;

  const goToConfirmation = () => {
    clearSplitPaymentInFlight();
    captureLastCompletedOrder(orderId);
    router.replace(checkoutPaths.confirmation(orderId));
  };

  try {
    const outcome = await resolveAuthorisedPaymentRedirectOutcome({
      orderId,
      checkoutId,
      totalAmount,
      countryCode,
      locale,
      payPerLinkToken,
      transactionData,
      logPrefix,
    });

    switch (outcome.kind) {
      case 'pay-per-link':
        router.replace(outcome.url);
        return;
      case 'remaining-balance-missing-token':
        if (onRemainingBalanceMissingToken) {
          onRemainingBalanceMissingToken();
          return;
        }
        goToConfirmation();
        return;
      case 'confirmation':
        goToConfirmation();
        return;
    }
  } catch (err) {
    devError(`${logPrefix} Error checking split payment:`, err);

    // Confirmation would present a partially paid order as complete (BEC-24895),
    // so an unreadable balance keeps the shopper in the payment flow.
    if (hasSplitPaymentInFlight(orderId) && onRemainingBalanceMissingToken) {
      onRemainingBalanceMissingToken();
      return;
    }

    goToConfirmation();
  }
}
